/**
 * Performance Monitoring Service
 *
 * Monitors and optimizes application performance for the Super Admin Dashboard
 */

import React from 'react';
import { logger } from './logger';

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: number;
  tags?: Record<string, string>;
}

export interface ResourceTiming {
  name: string;
  duration: number;
  size?: number;
  type: 'fetch' | 'navigation' | 'resource';
}

export interface ComponentPerformance {
  componentName: string;
  renderTime: number;
  mountTime?: number;
  updateCount: number;
  lastUpdate: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private componentMetrics: Map<string, ComponentPerformance> = new Map();
  private observer: PerformanceObserver | null = null;
  private isMonitoring = false;
  private reportingInterval: number | null = null;

  constructor() {
    this.setupPerformanceObserver();
    this.startReporting();
  }

  private setupPerformanceObserver(): void {
    if (!('PerformanceObserver' in window)) {
      logger.warn('PerformanceObserver not supported');
      return;
    }

    try {
      this.observer = new PerformanceObserver(list => {
        const entries = list.getEntries();

        entries.forEach(entry => {
          this.processPerformanceEntry(entry);
        });
      });

      // Observe different types of performance entries
      this.observer.observe({
        entryTypes: ['navigation', 'resource', 'measure', 'paint', 'largest-contentful-paint'],
      });

      this.isMonitoring = true;
    } catch (error) {
      logger.error('Failed to setup performance observer', error);
    }
  }

  private processPerformanceEntry(entry: PerformanceEntry): void {
    switch (entry.entryType) {
      case 'navigation':
        this.handleNavigationTiming(entry as PerformanceNavigationTiming);
        break;
      case 'resource':
        this.handleResourceTiming(entry as PerformanceResourceTiming);
        break;
      case 'measure':
        this.handleMeasure(entry);
        break;
      case 'paint':
        this.handlePaintTiming(entry);
        break;
      case 'largest-contentful-paint':
        this.handleLCPTiming(entry);
        break;
    }
  }

  private handleNavigationTiming(entry: PerformanceNavigationTiming): void {
    const metrics = [
      { name: 'dns_lookup', value: entry.domainLookupEnd - entry.domainLookupStart },
      { name: 'tcp_connect', value: entry.connectEnd - entry.connectStart },
      { name: 'request_response', value: entry.responseEnd - entry.requestStart },
      { name: 'dom_processing', value: entry.domComplete - entry.domInteractive },
      { name: 'page_load', value: entry.loadEventEnd - entry.fetchStart },
    ];

    metrics.forEach(metric => {
      this.recordMetric(metric.name, metric.value, 'ms', { type: 'navigation' });
    });

    // Log slow page loads
    const totalLoadTime = entry.loadEventEnd - entry.fetchStart;
    if (totalLoadTime > 3000) {
      logger.performanceMetric('slow_page_load', totalLoadTime, 'ms');
    }
  }

  private handleResourceTiming(entry: PerformanceResourceTiming): void {
    const duration = entry.responseEnd - entry.startTime;
    const size = entry.transferSize || 0;

    // Categorize resources
    let resourceType = 'other';
    if (entry.name.includes('/api/')) {
      resourceType = 'api';
    } else if (entry.name.match(/\.(js|jsx|ts|tsx)$/)) {
      resourceType = 'script';
    } else if (entry.name.match(/\.(css)$/)) {
      resourceType = 'stylesheet';
    } else if (entry.name.match(/\.(png|jpg|jpeg|gif|svg|webp)$/)) {
      resourceType = 'image';
    }

    this.recordMetric('resource_load_time', duration, 'ms', {
      type: resourceType,
      url: entry.name,
    });

    // Log slow API calls
    if (resourceType === 'api' && duration > 2000) {
      logger.performanceMetric('slow_api_call', duration, 'ms');
      logger.warn('Slow API call detected', {
        url: entry.name,
        duration,
        size,
      });
    }

    // Log large resources
    if (size > 1024 * 1024) {
      // 1MB
      logger.performanceMetric('large_resource', size, 'bytes');
      logger.warn('Large resource detected', {
        url: entry.name,
        size,
        duration,
      });
    }
  }

  private handleMeasure(entry: PerformanceEntry): void {
    this.recordMetric(entry.name, entry.duration, 'ms', { type: 'measure' });
  }

  private handlePaintTiming(entry: PerformanceEntry): void {
    this.recordMetric(entry.name, entry.startTime, 'ms', { type: 'paint' });
  }

  private handleLCPTiming(entry: PerformanceEntry): void {
    this.recordMetric('largest_contentful_paint', entry.startTime, 'ms', { type: 'lcp' });

    // Log poor LCP scores
    if (entry.startTime > 2500) {
      logger.performanceMetric('poor_lcp', entry.startTime, 'ms');
    }
  }

  /**
   * Record a custom performance metric
   */
  recordMetric(name: string, value: number, unit: string, tags?: Record<string, string>): void {
    const metric: PerformanceMetric = {
      name,
      value,
      unit,
      timestamp: Date.now(),
      tags,
    };

    this.metrics.push(metric);

    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }

    logger.performanceMetric(name, value, unit);
  }

  /**
   * Start measuring a custom operation
   */
  startMeasure(name: string): void {
    if ('performance' in window && 'mark' in performance) {
      performance.mark(`${name}-start`);
    }
  }

  /**
   * End measuring a custom operation
   */
  endMeasure(name: string): number {
    if ('performance' in window && 'mark' in performance && 'measure' in performance) {
      const startMark = `${name}-start`;
      const endMark = `${name}-end`;

      performance.mark(endMark);
      performance.measure(name, startMark, endMark);

      const measure = performance.getEntriesByName(name, 'measure')[0];
      return measure ? measure.duration : 0;
    }
    return 0;
  }

  /**
   * Record React component performance
   */
  recordComponentPerformance(
    componentName: string,
    renderTime: number,
    isMount = false,
    isUpdate = false
  ): void {
    const existing = this.componentMetrics.get(componentName);

    if (existing) {
      existing.renderTime = renderTime;
      existing.lastUpdate = Date.now();
      if (isUpdate) {
        existing.updateCount++;
      }
    } else {
      this.componentMetrics.set(componentName, {
        componentName,
        renderTime,
        mountTime: isMount ? renderTime : undefined,
        updateCount: isUpdate ? 1 : 0,
        lastUpdate: Date.now(),
      });
    }

    // Log slow component renders
    if (renderTime > 16) {
      // More than one frame at 60fps
      logger.performanceMetric('slow_component_render', renderTime, 'ms');
      logger.warn('Slow component render detected', {
        componentName,
        renderTime,
        isMount,
        isUpdate,
      });
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    metrics: PerformanceMetric[];
    components: ComponentPerformance[];
    summary: {
      avgApiResponseTime: number;
      slowApiCalls: number;
      avgComponentRenderTime: number;
      slowComponents: number;
      totalMetrics: number;
    };
  } {
    const apiMetrics = this.metrics.filter(m => m.tags?.type === 'api');
    const componentMetrics = Array.from(this.componentMetrics.values());

    const avgApiResponseTime =
      apiMetrics.length > 0
        ? apiMetrics.reduce((sum, m) => sum + m.value, 0) / apiMetrics.length
        : 0;

    const slowApiCalls = apiMetrics.filter(m => m.value > 2000).length;

    const avgComponentRenderTime =
      componentMetrics.length > 0
        ? componentMetrics.reduce((sum, c) => sum + c.renderTime, 0) / componentMetrics.length
        : 0;

    const slowComponents = componentMetrics.filter(c => c.renderTime > 16).length;

    return {
      metrics: this.metrics.slice(-100), // Return last 100 metrics
      components: componentMetrics,
      summary: {
        avgApiResponseTime,
        slowApiCalls,
        avgComponentRenderTime,
        slowComponents,
        totalMetrics: this.metrics.length,
      },
    };
  }

  /**
   * Get Core Web Vitals
   */
  getCoreWebVitals(): Promise<{
    lcp?: number;
    fid?: number;
    cls?: number;
  }> {
    return new Promise(resolve => {
      const vitals: { lcp?: number; fid?: number; cls?: number } = {};

      // Get LCP
      const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
      if (lcpEntries.length > 0) {
        vitals.lcp = lcpEntries[lcpEntries.length - 1].startTime;
      }

      // Get FID (First Input Delay)
      if ('PerformanceEventTiming' in window) {
        const observer = new PerformanceObserver(list => {
          const entries = list.getEntries() as PerformanceEventTiming[];
          entries.forEach(entry => {
            if (entry.name === 'first-input') {
              vitals.fid = entry.processingStart - entry.startTime;
            }
          });
          observer.disconnect();
          resolve(vitals);
        });

        observer.observe({ type: 'first-input', buffered: true });

        // Timeout after 5 seconds
        setTimeout(() => {
          observer.disconnect();
          resolve(vitals);
        }, 5000);
      } else {
        resolve(vitals);
      }
    });
  }

  /**
   * Monitor memory usage
   */
  getMemoryUsage(): {
    used: number;
    total: number;
    percentage: number;
  } | null {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        percentage: (memory.usedJSHeapSize / memory.totalJSHeapSize) * 100,
      };
    }
    return null;
  }

  private startReporting(): void {
    // Report performance metrics every 30 seconds
    this.reportingInterval = window.setInterval(() => {
      this.reportMetrics();
    }, 30000);
  }

  private reportMetrics(): void {
    const summary = this.getPerformanceSummary();
    const memory = this.getMemoryUsage();

    logger.businessEvent('performance_report', {
      ...summary.summary,
      memoryUsage: memory,
      timestamp: Date.now(),
    });

    // Clear old metrics to prevent memory leaks
    this.metrics = this.metrics.slice(-500);
  }

  /**
   * Cleanup performance monitoring
   */
  cleanup(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.reportingInterval) {
      clearInterval(this.reportingInterval);
      this.reportingInterval = null;
    }

    this.isMonitoring = false;
  }

  /**
   * Check if monitoring is active
   */
  isActive(): boolean {
    return this.isMonitoring;
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for performance monitoring
export const usePerformanceMonitor = () => {
  return {
    recordMetric: performanceMonitor.recordMetric.bind(performanceMonitor),
    startMeasure: performanceMonitor.startMeasure.bind(performanceMonitor),
    endMeasure: performanceMonitor.endMeasure.bind(performanceMonitor),
    recordComponentPerformance:
      performanceMonitor.recordComponentPerformance.bind(performanceMonitor),
    getPerformanceSummary: performanceMonitor.getPerformanceSummary.bind(performanceMonitor),
    getCoreWebVitals: performanceMonitor.getCoreWebVitals.bind(performanceMonitor),
    getMemoryUsage: performanceMonitor.getMemoryUsage.bind(performanceMonitor),
    isActive: performanceMonitor.isActive.bind(performanceMonitor),
  };
};

// Higher-order component for measuring component performance
export function withPerformanceMonitoring<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) {
  const displayName =
    componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

  return React.memo((props: P) => {
    const renderStartTime = performance.now();

    React.useEffect(() => {
      const renderEndTime = performance.now();
      const renderTime = renderEndTime - renderStartTime;

      performanceMonitor.recordComponentPerformance(displayName, renderTime, true, false);
    }, []);

    React.useEffect(() => {
      const renderEndTime = performance.now();
      const renderTime = renderEndTime - renderStartTime;

      performanceMonitor.recordComponentPerformance(displayName, renderTime, false, true);
    });

    return React.createElement(WrappedComponent, props);
  });
}

// Performance measurement decorator for functions
export function measurePerformance<T extends (...args: any[]) => any>(fn: T, name?: string): T {
  const measureName = name || fn.name || 'anonymous_function';

  return ((...args: Parameters<T>) => {
    performanceMonitor.startMeasure(measureName);

    try {
      const result = fn(...args);

      // Handle async functions
      if (result instanceof Promise) {
        return result.finally(() => {
          performanceMonitor.endMeasure(measureName);
        });
      }

      performanceMonitor.endMeasure(measureName);
      return result;
    } catch (error) {
      performanceMonitor.endMeasure(measureName);
      throw error;
    }
  }) as T;
}
